import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import "./ChemicalPage.css";
import { getApiBase } from "../api/healthThreatsApi";

const API_BASE = getApiBase();

const ACTIVE_JOB_KEY =
  "chemical_active_training_job";

const LAST_RESULT_KEY =
  "chemical_last_training_result";

const TRAINING_STORAGE_VERSION = 3;

const CHEMICAL_DATASET_COLUMNS = [
  "Data_Origin",
  "Scenario_ID",
  "Observation_Time",
  "Time_Unit",
  "Wind_Speed",
  "Temperature",
  "Humidity",
  "Precipitation",
  "Release_Rate",
  "Leak_Duration",
  "Downwind_Distance",
  "Crosswind_Distance",
  "log_hcl",
  "Country",
];

const DEFAULT_FORM = {
  center_lat: "51.7287407121588",
  center_lon: "-8.57651990177437",
  start_time: "2026-02-27T14:30",
  simulation_duration_mins: "180",
  wind_speed_ms: "4.16",
  wind_direction_deg: "315",
  temperature_c: "12",
  humidity: "0.75",
  precipitation_mm_hr: "0",
  release_mass_kg: "175",
  leak_duration_mins: "180",
};

const FORM_FIELDS = [
  [
    "center_lat",
    "Center latitude",
    "number",
    "any",
  ],
  [
    "center_lon",
    "Center longitude",
    "number",
    "any",
  ],
  [
    "start_time",
    "Start time",
    "datetime-local",
  ],
  [
    "simulation_duration_mins",
    "Simulation duration (minutes)",
    "number",
    "1",
  ],
  [
    "wind_speed_ms",
    "Wind speed (m/s)",
    "number",
    "any",
  ],
  [
    "wind_direction_deg",
    "Wind direction (degrees)",
    "number",
    "any",
  ],
  [
    "temperature_c",
    "Temperature (°C)",
    "number",
    "any",
  ],
  [
    "humidity",
    "Humidity (0–1)",
    "number",
    "0.01",
  ],
  [
    "precipitation_mm_hr",
    "Precipitation (mm/h)",
    "number",
    "any",
  ],
  [
    "release_mass_kg",
    "Release mass (kg)",
    "number",
    "any",
  ],
  [
    "leak_duration_mins",
    "Leak duration (minutes)",
    "number",
    "any",
  ],
];

async function apiErrorToMessage(
  response
) {
  const fallback =
    `Request failed (${response.status})`;

  try {
    const contentType =
      response.headers.get(
        "content-type"
      ) || "";

    if (
      contentType.includes(
        "application/json"
      )
    ) {
      const payload =
        await response
          .json()
          .catch(() => null);

      const detail =
        payload?.detail
        ?? payload?.message
        ?? payload?.error;

      if (typeof detail === "string") {
        return detail;
      }

      if (detail) {
        return JSON.stringify(
          detail,
          null,
          2
        );
      }

      return fallback;
    }

    const text =
      await response
        .text()
        .catch(() => "");

    return text.trim() || fallback;

  } catch {
    return fallback;
  }
}

function loadStored(key) {
  try {
    const raw =
      sessionStorage.getItem(key);

    return raw
      ? JSON.parse(raw)
      : null;

  } catch {
    return null;
  }
}

function saveStored(
  key,
  value
) {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify(value)
    );
  } catch {}
}

function removeStored(key) {
  try {
    sessionStorage.removeItem(key);
  } catch {}
}

function numberValue(
  value,
  label
) {
  const parsed = Number(value);

  if (!Number.isFinite(parsed)) {
    throw new Error(
      `${label} must be a valid number.`
    );
  }

  return parsed;
}

export default function ChemicalPage() {
  const country = "IRELAND";

  const [
    modelAvailability,
    setModelAvailability,
  ] = useState({
    default: false,
    custom: false,
  });

  const [
    modelType,
    setModelType,
  ] = useState("default");

  const [
    modelsChecking,
    setModelsChecking,
  ] = useState(true);

  const [
    trainingStatus,
    setTrainingStatus,
  ] = useState(null);

  const [
    trainingPanelOpen,
    setTrainingPanelOpen,
  ] = useState(false);

  const [
    trainingMode,
    setTrainingMode,
  ] = useState("existing");

  const [
    customDatasetFile,
    setCustomDatasetFile,
  ] = useState(null);

  const [
    trainingMessage,
    setTrainingMessage,
  ] = useState(null);

  const [
    trainingProgress,
    setTrainingProgress,
  ] = useState(0);

  const [
    trainingJobId,
    setTrainingJobId,
  ] = useState(null);

  const [
    predictionOpen,
    setPredictionOpen,
  ] = useState(false);

  const [
    predictionStatus,
    setPredictionStatus,
  ] = useState(null);

  const [
    predictionMessage,
    setPredictionMessage,
  ] = useState(null);

  const [
    form,
    setForm,
  ] = useState(DEFAULT_FORM);

  const [
    mapAsset,
    setMapAsset,
  ] = useState(null);

  const [
    mapStatus,
    setMapStatus,
  ] = useState(null);

  const [
    mapMessage,
    setMapMessage,
  ] = useState(null);

  const [
    mapVersion,
    setMapVersion,
  ] = useState(0);

  const trainingPollRef =
    useRef(null);

  const predictionAbortRef =
    useRef(null);

  const modelsReady = (
    modelAvailability.default
    || modelAvailability.custom
  );

  const selectedModelReady =
    Boolean(
      modelAvailability[modelType]
    );

  const stopTrainingPolling = () => {
    if (trainingPollRef.current) {
      clearInterval(
        trainingPollRef.current
      );

      trainingPollRef.current = null;
    }
  };

  const checkModels = async () => {
    setModelsChecking(true);

    try {
      const checkModel = async (
        requestedModelType
      ) => {
        const query =
          new URLSearchParams({
            country,
            threat: "Chem",
            model_type:
              requestedModelType,
          });

        const response = await fetch(
          (
            `${API_BASE}/models/exist`
            + `?${query.toString()}`
          ),
          {
            cache: "no-store",
          }
        );

        if (!response.ok) {
          throw new Error(
            await apiErrorToMessage(
              response
            )
          );
        }

        const payload =
          await response.json();

        return Boolean(
          payload.ready_for_prediction
        );
      };

      const [
        defaultReady,
        customReady,
      ] = await Promise.all([
        checkModel("default"),
        checkModel("custom"),
      ]);

      const availability = {
        default: defaultReady,
        custom: customReady,
      };

      setModelAvailability(
        availability
      );

      setModelType((current) => {
        if (availability[current]) {
          return current;
        }

        if (availability.default) {
          return "default";
        }

        if (availability.custom) {
          return "custom";
        }

        return "default";
      });

    } catch {
      setModelAvailability({
        default: false,
        custom: false,
      });

    } finally {
      setModelsChecking(false);
    }
  };

  const finishTraining = (
    job,
    jobId
  ) => {
    const activeJob = loadStored(
      ACTIVE_JOB_KEY
    );
    const completedMode =
      job?.metadata?.training_mode
      || activeJob?.trainingMode
      || trainingMode;
    const completedModelType =
      completedMode === "custom"
        ? "custom"
        : "default";
    const datasetId =
      completedMode === "custom"
        ? (
          job?.metadata?.filename
          || activeJob?.datasetId
          || "Uploaded Chemical dataset"
        )
        : "Available Chemical data";

    const result = {
      version: TRAINING_STORAGE_VERSION,

      jobId:
        job.job_id || jobId,

      country:
        job.country || country,

      disease:
        job.threat || "Chem",

      status: "completed",

      completedAt:
        new Date().toISOString(),

      message:
        job.message
        || (
          completedMode === "custom"
            ? (
              "Custom Chemical model "
              + "trained successfully."
            )
            : (
              "Existing Chemical model "
              + "retrained successfully."
            )
        ),

      datasetId,

      datasetSource: "ready",
      trainingSource:
        completedMode === "custom"
          ? "upload"
          : "idl",
      trainingMode: completedMode,
      modelType: completedModelType,
    };

    saveStored(
      LAST_RESULT_KEY,
      result
    );

    removeStored(
      ACTIVE_JOB_KEY
    );

    setTrainingStatus(
      "success"
    );

    setTrainingMessage(
      result.message
    );

    setTrainingProgress(100);
    setModelAvailability(
      (current) => ({
        ...current,
        [completedModelType]: true,
      })
    );
    setModelType(
      completedModelType
    );
    setTrainingPanelOpen(true);
    checkModels();
  };

  const startTrainingPolling = (
    jobId
  ) => {
    if (!jobId) {
      return;
    }

    stopTrainingPolling();

    const poll = async () => {
      try {
        const response = await fetch(
          (
            `${API_BASE}/train/jobs/`
            + jobId
          ),
          {
            cache: "no-store",
          }
        );

        if (!response.ok) {
          throw new Error(
            await apiErrorToMessage(
              response
            )
          );
        }

        const job =
          await response.json();

        setTrainingJobId(
          job.job_id || jobId
        );

        setTrainingProgress(
          Math.max(
            0,
            Math.min(
              100,
              Number(
                job.progress || 0
              )
            )
          )
        );

        if (
          job.status === "queued"
          || job.status === "running"
        ) {
          setTrainingStatus(
            "loading"
          );

          setTrainingMessage(
            job.message
            || (
              "Chemical training "
              + "in progress..."
            )
          );

          return;
        }

        stopTrainingPolling();

        removeStored(
          ACTIVE_JOB_KEY
        );

        if (
          job.status === "completed"
        ) {
          finishTraining(
            job,
            jobId
          );

          return;
        }

        const message =
          job.status === "cancelled"
            ? (
              job.message
              || (
                "Chemical training "
                + "cancelled."
              )
            )
            : (
              job.error
              || job.message
              || (
                "Chemical training "
                + "failed."
              )
            );

        saveStored(
          LAST_RESULT_KEY,
          {
            version:
              TRAINING_STORAGE_VERSION,

            jobId:
              job.job_id || jobId,

            country,
            disease: "Chem",
            status: job.status,

            completedAt:
              new Date()
                .toISOString(),

            message,

            datasetId:
              job?.metadata?.filename
              || "Available Chemical data",

            datasetSource:
              "ready",

            trainingSource:
              job?.metadata?.source
              || "unknown",

            trainingMode:
              job?.metadata?.training_mode
              || trainingMode,
          }
        );

        setTrainingStatus(
          "error"
        );

        setTrainingMessage(
          message
        );

        setTrainingProgress(0);

      } catch (error) {
        stopTrainingPolling();

        setTrainingStatus(
          "error"
        );

        setTrainingMessage(
          String(
            error?.message
            || error
          )
        );
      }
    };

    poll();

    trainingPollRef.current =
      setInterval(
        poll,
        2500
      );
  };

  const startTraining = async () => {
    if (
      trainingMode === "custom"
      && !customDatasetFile
    ) {
      setTrainingStatus("error");
      setTrainingMessage(
        "Please select a Chemical CDM CSV before starting training."
      );
      return;
    }

    setTrainingStatus(
      "loading"
    );

    setTrainingMessage(
      trainingMode === "custom"
        ? "Preparing your dataset..."
        : "Gathering the latest information for training..."
    );

    setTrainingProgress(1);

    removeStored(
      LAST_RESULT_KEY
    );

    try {
      const customTraining =
        trainingMode === "custom";
      const requestOptions = {
        method: "POST",
      };

      if (customTraining) {
        const formData = new FormData();
        formData.append(
          "dataset",
          customDatasetFile
        );
        requestOptions.body = formData;

      } else {
        requestOptions.headers = {
          "Content-Type":
            "application/json",
        };
        requestOptions.body =
          JSON.stringify({
            country,
            threat: "Chem",
            data_format: "json",
            data_source: null,
            json_data: null,
          });
      }

      const response = await fetch(
        (
          `${API_BASE}/train/jobs/chem/`
          + (
            customTraining
              ? "upload"
              : "idl"
          )
        ),
        requestOptions
      );

      if (!response.ok) {
        throw new Error(
          await apiErrorToMessage(
            response
          )
        );
      }

      const payload =
        await response.json();

      if (!payload?.job_id) {
        throw new Error(
          (
            "Training job started "
            + "without job_id."
          )
        );
      }

      saveStored(
        ACTIVE_JOB_KEY,
        {
          version:
            TRAINING_STORAGE_VERSION,

          jobId: payload.job_id,
          country,
          disease: "Chem",
          trainingMode,

          datasetId:
            customTraining
              ? customDatasetFile.name
              : "Available Chemical data",

          datasetSource:
            "ready",

          trainingSource:
            customTraining
              ? "upload"
              : "idl",

          startedAt:
            new Date()
              .toISOString(),
        }
      );

      setTrainingJobId(
        payload.job_id
      );

      setTrainingMessage(
        customTraining
          ? "Checking your dataset and getting training ready..."
          : "Getting everything ready..."
      );

      startTrainingPolling(
        payload.job_id
      );

    } catch (error) {
      removeStored(
        ACTIVE_JOB_KEY
      );

      setTrainingStatus(
        "error"
      );

      setTrainingMessage(
        String(
          error?.message
          || error
        )
      );

      setTrainingProgress(0);
    }
  };

  const cancelTraining = async () => {
    const activeJob =
      loadStored(
        ACTIVE_JOB_KEY
      );

    const jobId =
      trainingJobId
      || activeJob?.jobId;

    if (!jobId) {
      return;
    }

    try {
      const response = await fetch(
        (
          `${API_BASE}/train/jobs/`
          + `${jobId}/cancel`
        ),
        {
          method: "POST",
        }
      );

      if (!response.ok) {
        throw new Error(
          await apiErrorToMessage(
            response
          )
        );
      }

      setTrainingMessage(
        "Cancellation requested..."
      );

    } catch (error) {
      setTrainingStatus(
        "error"
      );

      setTrainingMessage(
        String(
          error?.message
          || error
        )
      );
    }
  };

  const loadMap = async () => {
    setMapStatus("loading");
    setMapMessage(null);
    setMapAsset(null);

    try {
      const query =
        new URLSearchParams({
          prefer: "html",
        });

      const response = await fetch(
        (
          `${API_BASE}`
          + "/visualizations/chemical"
          + `?${query.toString()}`
        ),
        {
          cache: "no-store",
        }
      );

      if (!response.ok) {
        throw new Error(
          await apiErrorToMessage(
            response
          )
        );
      }

      const payload =
        await response.json();

      const assets =
        Array.isArray(
          payload.assets
        )
          ? payload.assets
          : [];

      const asset =
        assets.find(
          (item) => (
            item?.kind === "map"
            && item?.format === "html"
          )
        );

      if (!asset) {
        throw new Error(
          (
            "No Chemical prediction "
            + "map is available."
          )
        );
      }

      setMapAsset(asset);
      setMapVersion(Date.now());
      setMapStatus("success");

    } catch (error) {
      setMapStatus("error");

      setMapMessage(
        String(
          error?.message
          || error
        )
      );
    }
  };

  const runPrediction = async () => {
    if (!selectedModelReady) {
      return;
    }

    if (
      predictionAbortRef.current
    ) {
      predictionAbortRef.current
        .abort();
    }

    const controller =
      new AbortController();

    predictionAbortRef.current =
      controller;

    setPredictionStatus(
      "loading"
    );

    setPredictionMessage(
      (
        "Running Chemical "
        + "prediction..."
      )
    );

    try {
      const payload = {
        country,
        threat: "Chem",
        model_type: modelType,

        center_lat:
          numberValue(
            form.center_lat,
            "Center latitude"
          ),

        center_lon:
          numberValue(
            form.center_lon,
            "Center longitude"
          ),

        start_time:
          form.start_time,

        simulation_duration_mins:
          numberValue(
            (
              form
                .simulation_duration_mins
            ),
            "Simulation duration"
          ),

        wind_speed_ms:
          numberValue(
            form.wind_speed_ms,
            "Wind speed"
          ),

        wind_direction_deg:
          numberValue(
            form.wind_direction_deg,
            "Wind direction"
          ),

        temperature_c:
          numberValue(
            form.temperature_c,
            "Temperature"
          ),

        humidity:
          numberValue(
            form.humidity,
            "Humidity"
          ),

        precipitation_mm_hr:
          numberValue(
            (
              form
                .precipitation_mm_hr
            ),
            "Precipitation"
          ),

        release_mass_kg:
          numberValue(
            form.release_mass_kg,
            "Release mass"
          ),

        leak_duration_mins:
          numberValue(
            form.leak_duration_mins,
            "Leak duration"
          ),
      };

      if (!payload.start_time) {
        throw new Error(
          "Start time is required."
        );
      }

      if (
        payload.humidity < 0
        || payload.humidity > 1
      ) {
        throw new Error(
          (
            "Humidity must be "
            + "between 0 and 1."
          )
        );
      }

      if (
        payload
          .simulation_duration_mins
        <= 0
      ) {
        throw new Error(
          (
            "Simulation duration must "
            + "be greater than zero."
          )
        );
      }

      if (
        payload.release_mass_kg <= 0
        || payload.leak_duration_mins <= 0
      ) {
        throw new Error(
          (
            "Release mass and leak "
            + "duration must be "
            + "greater than zero."
          )
        );
      }

      const response = await fetch(
        (
          `${API_BASE}`
          + "/predict_chem_models_static"
        ),
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body:
            JSON.stringify(payload),

          signal:
            controller.signal,
        }
      );

      if (!response.ok) {
        throw new Error(
          await apiErrorToMessage(
            response
          )
        );
      }

      const result =
        await response.json();

      setPredictionStatus(
        "success"
      );

      setPredictionMessage(
        (
          "Chemical prediction "
          + "completed successfully: "
          + (
            result.result_title
            || modelType
          )
        )
      );

      if (
        result.visualization_url
      ) {
        setMapAsset({
          title:
            result.result_title
            || "Chemical prediction",
          kind: "map",
          format: "html",
          filename:
            result.output_filename,
          url:
            result.visualization_url,
        });
        setMapVersion(Date.now());
        setMapStatus("success");
        setMapMessage(null);

      } else {
        await loadMap();
      }

    } catch (error) {
      setPredictionStatus(
        "error"
      );

      setPredictionMessage(
        error?.name === "AbortError"
          ? (
            "Chemical prediction "
            + "cancelled."
          )
          : String(
            error?.message
            || error
          )
      );

    } finally {
      predictionAbortRef.current =
        null;
    }
  };

  useEffect(() => {
    checkModels();

    const activeJob =
      loadStored(
        ACTIVE_JOB_KEY
      );

    if (
      activeJob?.version
      === TRAINING_STORAGE_VERSION
      && activeJob?.jobId
    ) {
      setTrainingPanelOpen(true);
      setTrainingMode(
        activeJob.trainingMode
        || "existing"
      );
      setTrainingStatus(
        "loading"
      );

      setTrainingMessage(
        (
          "Reconnecting to Chemical "
          + "training job..."
        )
      );

      setTrainingProgress(1);

      setTrainingJobId(
        activeJob.jobId
      );

      startTrainingPolling(
        activeJob.jobId
      );

      return;
    }

    if (activeJob) {
      removeStored(ACTIVE_JOB_KEY);
    }

    const lastResult =
      loadStored(
        LAST_RESULT_KEY
      );

    if (
      lastResult?.version
      === TRAINING_STORAGE_VERSION
      &&
      lastResult?.status
      === "completed"
    ) {
      const completedModelType =
        lastResult.modelType
        || (
          lastResult.trainingMode
          === "custom"
            ? "custom"
            : "default"
        );

      setTrainingPanelOpen(true);
      setTrainingMode(
        lastResult.trainingMode
        || "existing"
      );
      setTrainingStatus(
        "success"
      );

      setTrainingMessage(
        lastResult.message
      );

      setTrainingProgress(100);
      setModelAvailability(
        (current) => ({
          ...current,
          [completedModelType]: true,
        })
      );
      setModelType(
        completedModelType
      );

    } else if (lastResult) {
      removeStored(LAST_RESULT_KEY);
    }

    // eslint-disable-next-line
    // react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (
        trainingPollRef.current
      ) {
        clearInterval(
          trainingPollRef.current
        );
      }

      if (
        predictionAbortRef.current
      ) {
        predictionAbortRef.current
          .abort();
      }
    };
  }, []);

  const mapUrl =
    mapAsset?.url
      ? (
        `${API_BASE}${mapAsset.url}`
        + `?v=${mapVersion}`
      )
      : null;

  return (
    <div className="pageContainer">
      <div className="ds-header">
        <h1 className="ds-title">
          Chemical Agents
        </h1>
      </div>

      <div className="cp-stack">
        <div className="ds-card cp-filters">
          <div className="cp-filter-summary">
            <div className="cp-model-state">
              <span>
                AI model status
              </span>

              <div className="cp-model-statuses">
                <strong
                  className={
                    modelAvailability.default
                      ? "cp-ready"
                      : "cp-missing"
                  }
                >
                  {
                    modelsChecking
                      ? "Default: Checking..."
                      : modelAvailability.default
                      ? "Default: Ready"
                      : "Default: Missing"
                  }
                </strong>

                <strong
                  className={
                    modelAvailability.custom
                      ? "cp-ready"
                      : "cp-missing"
                  }
                >
                  {
                    modelsChecking
                      ? "Custom: Checking..."
                      : modelAvailability.custom
                      ? "Custom: Ready"
                      : "Custom: Not trained"
                  }
                </strong>
              </div>
            </div>
          </div>

          <div className="cp-actions">
            <button
              className="ds-btn"
              type="button"
              onClick={() => {
                setTrainingPanelOpen(
                  (value) => !value
                );
              }}
              disabled={
                trainingStatus
                === "loading"
              }
            >
              Create New AI Models
            </button>

            <button
              className={
                `ds-btn ${
                  !modelsReady
                    ? "ds-btn--disabled"
                    : ""
                }`
              }
              type="button"
              disabled={!modelsReady}
              onClick={() => {
                setPredictionOpen(
                  (value) => !value
                );
              }}
            >
              Adopt AI Models - Predict
            </button>

            <button
              className="ds-btn"
              type="button"
              onClick={loadMap}
            >
              Display Latest Prediction
            </button>
          </div>
        </div>

        {
          trainingPanelOpen
          && (
            <div className="ds-card cp-panel">
              <div className="cp-panel-header">
                <div>
                  <h2>
                    Chemical model training
                  </h2>

                  <p>
                    Choose the data source for
                    retraining the Chemical AI
                    model.
                  </p>
                </div>

                {
                  trainingStatus !== "loading"
                  && (
                    <button
                      className="ds-btn"
                      type="button"
                      onClick={() => {
                        setTrainingPanelOpen(
                          false
                        );
                      }}
                    >
                      Close
                    </button>
                  )
                }
              </div>

              {
                trainingStatus !== "loading"
                && (
                  <>
                    <fieldset className="cp-model-choice cp-training-choice">
                      <legend>
                        Training mode
                      </legend>

                      <label
                        className={
                          (
                            "cp-model-option "
                            + (
                              trainingMode
                              === "existing"
                                ? "cp-model-option--selected"
                                : ""
                            )
                          )
                        }
                      >
                        <input
                          type="radio"
                          name="chemical-training-mode"
                          value="existing"
                          checked={
                            trainingMode
                            === "existing"
                          }
                          onChange={() => {
                            setTrainingMode(
                              "existing"
                            );
                            setTrainingStatus(null);
                            setTrainingMessage(null);
                          }}
                        />

                        <span>
                          <strong>
                            {
                              "Retrain the existing model "
                              + "(for demonstration purposes)"
                            }
                          </strong>

                          <small>
                            Use the latest available
                            Chemical data automatically.
                          </small>
                        </span>
                      </label>

                      <label
                        className={
                          (
                            "cp-model-option "
                            + (
                              trainingMode
                              === "custom"
                                ? "cp-model-option--selected"
                                : ""
                            )
                          )
                        }
                      >
                        <input
                          type="radio"
                          name="chemical-training-mode"
                          value="custom"
                          checked={
                            trainingMode
                            === "custom"
                          }
                          onChange={() => {
                            setTrainingMode(
                              "custom"
                            );
                            setTrainingStatus(null);
                            setTrainingMessage(null);
                          }}
                        />

                        <span>
                          <strong>
                            {
                              "Retrain the model using "
                              + "custom data"
                            }
                          </strong>

                          <small>
                            Upload a Chemical CDM CSV
                            dataset from this computer.
                          </small>
                        </span>
                      </label>
                    </fieldset>

                    {
                      trainingMode === "custom"
                      && (
                        <div className="cp-upload-panel">
                          <label className="cp-upload-field">
                            <span>
                              Custom Chemical dataset
                            </span>

                            <input
                              type="file"
                              accept=".csv,text/csv"
                              onChange={(event) => {
                                setCustomDatasetFile(
                                  event.target.files?.[0]
                                  || null
                                );
                                setTrainingStatus(null);
                                setTrainingMessage(null);
                              }}
                            />
                          </label>

                          <small className="cp-upload-help">
                            CDM columns:{" "}
                            {
                              CHEMICAL_DATASET_COLUMNS
                                .join(", ")
                            }
                            . Country may be empty.{" "}
                            Scenario_ID is required for
                            model training. Maximum file size:
                            {" "}250 MB.
                          </small>
                        </div>
                      )
                    }

                    <div className="cp-form-actions">
                      <button
                        className="ds-btn"
                        type="button"
                        onClick={startTraining}
                        disabled={
                          trainingMode === "custom"
                          && !customDatasetFile
                        }
                      >
                        Start training
                      </button>
                    </div>
                  </>
                )
              }

              {
                trainingStatus === "loading"
                && (
                  <div className="cp-progress-card">
                <div className="cp-progress-header">
                  <span className="cp-progress-title">
                    <span
                      className="cp-spinner"
                      aria-hidden="true"
                    />

                    Training Chemical AI model
                  </span>

                  <button
                    className="ds-btn"
                    type="button"
                    onClick={cancelTraining}
                  >
                    Cancel training
                  </button>
                </div>

                <div className="cp-progress-message">
                  {trainingMessage}
                </div>

                <div className="cp-progress-meta">
                  <span>
                    Progress
                  </span>

                  <strong>
                    {trainingProgress}%
                  </strong>
                </div>

                <div className="cp-progress-track">
                  <div
                    className="cp-progress-bar"
                    style={{
                      width:
                        `${trainingProgress}%`,
                    }}
                  />
                </div>
                  </div>
                )
              }

              {
                trainingStatus === "success"
                && (
                  <div className="cp-message cp-message--success">
                    {trainingMessage}
                  </div>
                )
              }

              {
                trainingStatus === "error"
                && (
                  <div className="cp-message cp-message--error">
                    {trainingMessage}
                  </div>
                )
              }
            </div>
          )
        }

        {
          predictionOpen
          && (
            <div className="ds-card cp-panel">
              <div className="cp-panel-header">
                <div>
                  <h2>
                    Chemical prediction
                    parameters
                  </h2>

                  <p>
                    Configure the release
                    scenario and generate the
                    animated HCl AEGL map.
                  </p>
                </div>

                <button
                  className="ds-btn"
                  type="button"
                  onClick={() => {
                    setPredictionOpen(
                      false
                    );
                  }}
                >
                  Close
                </button>
              </div>

              <div className="cp-form-grid">
                <fieldset className="cp-model-choice">
                  <legend>
                    Prediction model
                  </legend>

                  <label
                    className={
                      (
                        "cp-model-option "
                        + (
                          modelType
                          === "default"
                            ? "cp-model-option--selected"
                            : ""
                        )
                        + (
                          !modelAvailability.default
                            ? " cp-model-option--disabled"
                            : ""
                        )
                      )
                    }
                  >
                    <input
                      type="radio"
                      name="chemical-model-type"
                      value="default"
                      checked={
                        modelType
                        === "default"
                      }
                      disabled={
                        !modelAvailability.default
                      }
                      onChange={() => {
                        setModelType(
                          "default"
                        );
                      }}
                    />

                    <span>
                      <strong>
                        Use Default Model
                      </strong>

                      <small>
                        Use the protected
                        pre-trained model.
                      </small>
                    </span>
                  </label>

                  <label
                    className={
                      (
                        "cp-model-option "
                        + (
                          modelType
                          === "custom"
                            ? "cp-model-option--selected"
                            : ""
                        )
                        + (
                          !modelAvailability.custom
                            ? " cp-model-option--disabled"
                            : ""
                        )
                      )
                    }
                  >
                    <input
                      type="radio"
                      name="chemical-model-type"
                      value="custom"
                      checked={
                        modelType
                        === "custom"
                      }
                      disabled={
                        !modelAvailability.custom
                      }
                      onChange={() => {
                        setModelType(
                          "custom"
                        );
                      }}
                    />

                    <span>
                      <strong>
                        Use Custom Model
                      </strong>

                      <small>
                        Use the latest model
                        trained by the user.
                      </small>
                    </span>
                  </label>
                </fieldset>

                {
                  FORM_FIELDS.map(
                    ([
                      name,
                      label,
                      type,
                      step,
                    ]) => (
                      <label
                        className="cp-field"
                        key={name}
                      >
                        <span>
                          {label}
                        </span>

                        <input
                          name={name}
                          type={type}
                          step={step}
                          value={form[name]}
                          onChange={(event) => {
                            setForm(
                              (current) => ({
                                ...current,
                                [name]:
                                  event.target.value,
                              })
                            );
                          }}
                        />
                      </label>
                    )
                  )
                }
              </div>

              <div className="cp-form-actions">
                <button
                  className="ds-btn"
                  type="button"
                  onClick={runPrediction}
                  disabled={
                    predictionStatus
                    === "loading"
                    || !selectedModelReady
                  }
                >
                  Start prediction
                </button>

                {
                  predictionStatus
                  === "loading"
                  && (
                    <button
                      className="ds-btn"
                      type="button"
                      onClick={() => {
                        predictionAbortRef
                          .current
                          ?.abort();
                      }}
                    >
                      Cancel prediction
                    </button>
                  )
                }
              </div>

              {
                predictionStatus
                && (
                  <div
                    className={
                      (
                        "cp-message "
                        + `cp-message--${
                          predictionStatus
                        }`
                      )
                    }
                  >
                    {
                      predictionStatus
                      === "loading"
                      && (
                        <span
                          className="cp-spinner"
                          aria-hidden="true"
                        />
                      )
                    }

                    {predictionMessage}
                  </div>
                )
              }
            </div>
          )
        }

        {
          mapStatus === "loading"
          && (
            <div className="ds-card cp-placeholder">
              Loading Chemical map...
            </div>
          )
        }

        {
          mapStatus === "error"
          && (
            <div className="ds-card cp-message cp-message--error">
              {mapMessage}
            </div>
          )
        }

        {
          mapStatus === "success"
          && mapUrl
          && (
            <div className="ds-card cp-map-card">
              <div className="cp-map-title">
                {
                  mapAsset.title
                  || (
                    "Chemical dispersion "
                    + "prediction"
                  )
                }
              </div>

              <iframe
                key={mapVersion}
                title={
                  mapAsset.title
                  || (
                    "Chemical dispersion "
                    + "prediction"
                  )
                }
                src={mapUrl}
                className="cp-map-frame"
              />
            </div>
          )
        }
      </div>
    </div>
  );
}
